import clsx from 'clsx';
import type { ResponseSpacing, Spacing } from '@athena-lib/spacing';
import {
  topPaddings,
  leftPaddings,
  negativeBottomMargins,
  negativeLeftMargins,
  responsiveTopPaddings,
  responsiveLeftPaddings,
  responsiveNegativeBottomMargins,
  responsiveNegativeLeftMargins,
} from '@athena-lib/spacing';
import type { DeviceName } from '@athena-lib/device';
import { objectEntries, objectKeys } from '@athena-lib/object-utils';

export const getPaddingClassName = (
  space: Spacing | ResponseSpacing | undefined,
  collapseBelow: Exclude<DeviceName, 'sm'> | undefined,
  collapse: boolean | undefined,
  reverseWhenCollapsed: boolean | undefined,
) => {
  if (!space) {
    return '';
  }
  const classes: string[] = [];
  if (collapse || collapseBelow) {
    if (reverseWhenCollapsed) {
      classes.push('last:pt-0');
    } else {
      classes.push('first:pt-0');
    }
  }
  if (typeof space === 'string') {
    classes.push(...getNonResponsivePadding(space, collapseBelow, collapse));
  } else {
    classes.push(...getResponsiveTopPadding(space, collapseBelow, collapse));
    classes.push(...getResponsiveLeftPadding(space));
  }
  return clsx(classes);
};

const getNonResponsivePadding = (
  space: Spacing,
  collapseBelow: string | undefined,
  collapse: boolean | undefined,
) => {
  const classes: string[] = [];
  classes.push(leftPaddings[space]);
  if (collapseBelow || collapse) {
    classes.push(topPaddings[space]);
    if (collapseBelow) {
      classes.push(responsiveTopPaddings[collapseBelow + '_none']);
    }
  }
  return classes;
};

const getResponsiveLeftPadding = (space: Partial<Record<DeviceName, Spacing>>) => {
  const classes: string[] = [];
  for (const deviceName of objectKeys(space)) {
    const deviceSpacing: Spacing = space[deviceName]!;
    if (deviceName === 'sm') {
      // Because we default to small screens
      classes.push(leftPaddings[deviceSpacing]);
    } else {
      classes.push(responsiveLeftPaddings[deviceName + '_' + deviceSpacing]);
    }
  }
  return classes;
};

const getResponsiveTopPadding = (
  space: Partial<Record<DeviceName, Spacing>>,
  collapseBelow: string | undefined,
  collapse: boolean | undefined,
) => {
  const classes: string[] = [];
  if (!collapseBelow && !collapse) {
    return classes;
  }

  let belowCollapse = true;
  for (const [deviceName, deviceSpacing] of objectEntries(space)) {
    if (collapseBelow === deviceName) {
      const noTopPadding = responsiveTopPaddings[collapseBelow + '_none'];
      classes.push(noTopPadding);
      belowCollapse = false;
    }

    if (belowCollapse) {
      classes.push(
        deviceName === 'sm'
          ? topPaddings[deviceSpacing!]
          : responsiveTopPaddings[deviceName + '_' + deviceSpacing],
      );
    }
  }
  return classes;
};

export const getNegativeMarginLeftClassName = (space: Spacing | ResponseSpacing | undefined) =>
  getNegativeMarginClassName(space, negativeLeftMargins, responsiveNegativeLeftMargins);

export const getNegativeMarginBottomClassName = (space: Spacing | ResponseSpacing | undefined) =>
  getNegativeMarginClassName(space, negativeBottomMargins, responsiveNegativeBottomMargins);

const getNegativeMarginClassName = (
  space: Spacing | ResponseSpacing | undefined,
  negativeMargins: Record<Spacing, string>,
  responsiveNegativeMargins: Record<string, string>,
) => {
  if (!space) {
    return '';
  }
  if (typeof space === 'string') {
    return negativeMargins[space];
  }
  const classes: string[] = [];
  for (const [deviceName, deviceSpace] of objectEntries(space)) {
    if (deviceName === 'sm') {
      // Because we default to small screens
      classes.push(negativeMargins[deviceSpace!]);
    } else {
      classes.push(responsiveNegativeMargins[deviceName + '_' + deviceSpace]);
    }
  }
  return clsx(classes);
};
